import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomTable from '../custom/CustomTableTemp';
import {
  Box,
  Button,
  Fade,
  Modal,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { getStatusesReports } from '../../services/settings_service';
import { BsUpload } from 'react-icons/bs';
import { setNotification } from '../../redux/notifications/ducks';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { uploadServiceReport } from '../../services/fleet_service';
import { setAccessTokenSelector } from '../../redux/users/ducks';
import { getWorkForceData } from '../../services/auth_service';

const useStyles = makeStyles()(theme => {
  return {
    container: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    modal: {
      alignSelf: 'center',
      top: '25% !important',
      maxWidth: '30%',
      borderRadius: 30,
      margin: '0 auto',
    },
    paper: {
      borderRadius: '20px',
      overflow: 'hidden',
    },
    topBarBox: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      margin: '0.5rem 0rem',

      borderRight: '1px solid #D1D5DB',
      padding: '0px 24px',
    },
    uploadFileBox: {
      marginTop: '20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      border: '2px solid black',
      borderRadius: '8px',
      backgroundColor: '#f9f9f9',
      width: '300px',
      padding: '10px',
      position: 'relative',
      textAlign: 'center',
    },
    uploadedFile: {
      color: '#000',
      textDecoration: 'none',
      fontSize: '12px',
      maxWidth: '120px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    removeFileBtn: {
      background: '#ff4d4d',
      border: 'none',
      color: 'white',
      fontSize: '14px',
      cursor: 'pointer',
      borderRadius: '50%',
      width: '20px',
      height: '20px',
      position: 'absolute',
      top: '5px',
      right: '5px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };
});

export const Compliance = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getRootProps, getInputProps } = useDropzone({
    accept: 'application/pdf',
    onDrop: acceptedFiles => {
      if (acceptedFiles[0].type === 'application/pdf') {
        setFileData(acceptedFiles[0]);
      } else {
        dispatch(setNotification('Invalid File Type', 'error', true));
      }
    },
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { classes } = useStyles();
  const [reportDetails, setReportDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [fileData, setFileData] = useState(null);
  const access_token = useSelector(setAccessTokenSelector);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalReports, setTotalReports] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [users, setUsers] = useState([]);

  const reportStatus = {
    SUCCESS: t('settingsPage.complianceSettings.success'),
    FAILURE: t('settingsPage.complianceSettings.failure'),
    IN_PROGRESS: t('settingsPage.complianceSettings.inProgress'),
  };

  const getUsers = useCallback(async () => {
    const response = await getWorkForceData();
    setUsers(response?.data?.resp?.workforceData);
  }, []);

  useEffect(() => {
    if (access_token) {
      getUsers();
    }
  }, [getUsers, access_token]);

  const userNames = userId => {
    if (users.length) {
      const user = users.find(user => user.user_id === userId);

      return user?.name;
    }
  };

  const rendrVehicleStatusDetails = (field, status) => {
    if (field === 'bg') {
      if (status === 'SUCCESS') {
        return 'var(--Light-green, #DEF2CC)';
      } else if (status === 'IN_PROGRESS') {
        return 'var(--extended-orange-100, #FFEDD5)';
      }
      return 'var(--default-gray-200, #E5E7EB)';
    }
    if (status === 'SUCCESS') {
      return 'var(--extended-lime-700, #4D7C0F)';
    } else if (status === 'IN_PROGRESS') {
      return 'var(--extended-orange-500, #F97316)';
    }
    return 'var(--default-gray-500, #6B7280)';
  };

  const columns = [
    {
      key: 'filename',
      title: t('settingsPage.complianceSettings.fileName'),
      canSort: false,
      dataHook: 'filename',
    },
    {
      key: 'user_id',
      title: t('settingsPage.complianceSettings.userId'),
      canSort: false,
      dataHook: 'tire-id',
      render: row => {
        return <Typography>{userNames(row.user_id)}</Typography>;
      },
    },
    {
      key: 'status',
      title: t('settingsPage.complianceSettings.status'),
      canSort: false,
      dataHook: 'status',
      render: row => {
        return (
          <Box className={classes.topBarBox}>
            <Box
              style={{
                padding: '2px 18px',
                borderRadius: '30px',
                backgroundColor: rendrVehicleStatusDetails('bg', row.status),

                color: rendrVehicleStatusDetails('color', row.status),
              }}
            >
              <Typography variant='body1'>
                {reportStatus[row.status]}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      key: 'description',
      title: t('settingsPage.complianceSettings.description'),
      canSort: false,
      dataHook: 'description',
    },
  ];

  const getStatusReports = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await getStatusesReports(
        access_token,
        currentPage,
        rowsPerPage
      );
      if (res.status === 200) {
        setReportDetails(res.data.resource.statusList);
        setTotalReports(res.data.resource.count);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [
    access_token,
    setIsLoading,
    setReportDetails,
    setTotalReports,
    rowsPerPage,
    currentPage,
  ]);

  useEffect(() => {
    if (access_token) {
      getStatusReports();
    }
  }, [access_token, rowsPerPage, getStatusReports]);

  const handleSubmit = async () => {
    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('document', fileData);
      const response = await uploadServiceReport('compliance', formData);
      if (response.status === 200) {
        dispatch(
          setNotification(
            t('settingsPage.complianceSettings.uploadSuccess'),
            'success',
            true
          )
        );
        getStatusReports();
        setOpenModal(false);
      } else {
        dispatch(
          setNotification(
            t('settingsPage.complianceSettings.notUpload'),
            'error',
            true
          )
        );
      }
    } catch (error) {
      dispatch(
        setNotification(
          t('settingsPage.complianceSettings.notUpload'),
          'error',
          true
        )
      );
    } finally {
      setIsUploading(false);
    }
  };

  const paginationHandler = async page => {
    try {
      const res = await getStatusesReports(access_token, page, rowsPerPage);
      if (res.status === 200) {
        setReportDetails(res.data.resource.statusList);
        setTotalReports(res.data.resource.count);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const renderFileSection = () => {
    if (fileData) {
      return (
        <Box className={classes.uploadFileBox} I data-hook='uploaded-file'>
          <Box
            style={{
              fontSize: '14px',
              fontWeight: 'bold',
              marginBottom: '10px',
            }}
          >
            {(fileData.size / (1024 * 1024)).toFixed(2)} MB
          </Box>
          <a
            href={URL.createObjectURL(fileData)}
            target='_blank'
            rel='noopener noreferrer'
            onClick={e => e.stopPropagation()}
            className={classes.uploadedFile}
          >
            {fileData.name}
          </a>
          <button
            onClick={e => {
              e.stopPropagation();
              setFileData(null);
            }}
            className={classes.removeFileBtn}
            data-hook='remove-file-btn'
          >
            &times;
          </button>
        </Box>
      );
    }

    return (
      <Typography>{t('settingsPage.complianceSettings.selectFile')}</Typography>
    );
  };

  const handleClose = () => {
    setOpenModal(false);
    setFileData(null);
  };
  return (
    <Box position={'relative'} zIndex={10}>
      <Box className={classes.container}>
        <Button
          style={{
            textTransform: 'unset',
            fontSize: 18,
          }}
          variant='contained'
          color='primary'
          startIcon={<BsUpload size={20} />}
          onClick={() => setOpenModal(true)}
          data-hook='upload-report-button'
        >
          {t('settingsPage.complianceSettings.uploadReport')}
        </Button>
      </Box>
      <CustomTable
        data={reportDetails}
        emptyMessage={t('settingsPage.complianceSettings.noReportsPresent')}
        paginationHandler={paginationHandler}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        loading={isLoading}
        columns={columns}
        total={totalReports}
        setCurrentPage={setCurrentPage}
        data-hook='compliance-table'
      />

      <Modal
        aria-labelledby='report-modal-title'
        aria-describedby='report-modal-description'
        className={classes.modal}
        open={openModal}
        onClose={handleClose}
        closeAfterTransition
        data-hook='report-upload-modal'
      >
        <Fade in={openModal}>
          <Paper className={classes.paper}>
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              height='100%'
              gap={'16px'}
              bgcolor='#F3F4F6'
              width='100%'
              padding={'30px 137px 30px 137px'}
            >
              <Typography>
                {t('settingsPage.complianceSettings.uploadReport')}
              </Typography>
              <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='center'
                height={72}
                width={72}
                borderRadius={16}
                bgcolor='#FFF'
              >
                <BsUpload size={28} />
              </Box>
              <Box
                {...getRootProps()}
                style={{
                  border: '2px dashed #ddd',
                  padding: '20px',
                  cursor: 'pointer',
                  borderRadius: '20px',
                }}
                data-hook='DnD-Box'
              >
                <input {...getInputProps()} />
                {renderFileSection()}
              </Box>
              <Button
                variant='contained'
                color='primary'
                data-hook='pdf-upload-btn'
                disableElevation
                disabled={!fileData}
                className={classes.uploadBtn}
                onClick={handleSubmit}
              >
                {isUploading ? (
                  <CircularProgress size={30} color='inherit' />
                ) : (
                  t('fleetsSection.upload')
                )}
              </Button>
            </Box>
          </Paper>
        </Fade>
      </Modal>
    </Box>
  );
};
